import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ZodError } from "zod";
import { useFormServerSideErrorsHandler } from "../components/form/ErrorsHandler/ErrorsHandler";
import { useAuth } from "../context/auth";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import { bookingsApiSchema } from "../schemas/bookings";
import { statusApiSchema } from "../schemas/status";

export const bookingsApi = {
    getBookings: (axios, params) => {
        return axios
            .get(apiUrls.bookings.all, {
                params,
            })
            .then((response) => response.data)
            // Below trow error beacouse reduce some data in related objects 
            // .then((response) => bookingsApiSchema.getBookingsResponse.parse(response.data));
    },
    getBookingById: (axios, bookingId) => {
        return axios
            .get(apiUrls.bookings.booking(bookingId))
            .then((response) => response.data);
            // .then((response) => bookingsApiSchema.bookingResponseEntry.parse(response.data));
    },
    async getStatuses(axios) {
        return axios
            .get(apiUrls.bookings.statuses)
            .then((response) => statusApiSchema.getStatusesResponse.parse(response.data));
    },
    createBooking: (axios, body) => {
        return axios.post(
            apiUrls.bookings.create,
            bookingsApiSchema.createBookingRequest.parse(body),
        );
    },
    updateBooking: (axios, bookingId, body) => {
        return axios.put(
            apiUrls.bookings.update(bookingId),
            bookingsApiSchema.putUpdateBookingRequest.parse(body),
        );
    },

    async getCancellationReasons(axios) {
        return axios
            .get(apiUrls.bookings.cancellationReasons)
            .then((response) =>
                bookingsApiSchema.getBookingCancellationReasonsResponse.parse(response.data),
            );
    },

    async cancelBooking(axios, bookingId, reasonId, resonNote) {
        const body = { reason_id: reasonId, status_note: resonNote };
        return axios
            .post(
                apiUrls.bookings.cancel(bookingId),
                bookingsApiSchema.cancelBookingRequest.parse(body),
            )
            .then((response) => response.data);
    },

    async getPostponedBookings(axios) {
        return axios
            .get(apiUrls.bookings.postponed)
            .then((response) => bookingsApiSchema.getBookingPostponedResponse.parse(response.data));
    },

    getCustomerBookings: (axios, customerId, params = new URLSearchParams()) => {
        return axios
            .get(apiUrls.bookings.customer(customerId), {
                params,
            })
            .then((response) => response.data);
            // .then((response) => bookingsApiSchema.getCustomerBookingsResponse.parse(response.data));
    },
};

export function useBookings(
    params = new URLSearchParams(),
    options = {
        queryKey: ["booking-list"],
    },
) {
    const axiosPriv = useAxiosPrivate();

    const { queryKey: _, ...restOptions } = options;

    return useQuery({
        queryKey: ["bookings", Object.fromEntries(params.entries())],
        queryFn: () => {
            return bookingsApi.getBookings(axiosPriv, params);
        },
        throwOnError: (error) => error instanceof ZodError,
        ...restOptions,
    });
}

export function useBookingById(bookingId, options = {}) {
    const axiosPriv = useAxiosPrivate();
    return useQuery({
        queryKey: ["bookings", bookingId],
        queryFn: () => {
            return bookingsApi.getBookingById(axiosPriv, bookingId);
        },
        ...options,
    });
}

export function useBookingsForOffer(
    offerId,
    params = new URLSearchParams(),
    options = {
        queryKey: ["booking-list"],
    },
) {
    params.set("offer_id", offerId);
    return useBookings(params, options);
}

export function useBookingsStatuses(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["bookingStatuses"],
        queryFn: () => {
            return bookingsApi.getStatuses(axiosPriv);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useBookingCancellationReasons(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["bookingCancellationReasons"],
        queryFn: () => {
            return bookingsApi.getCancellationReasons(axiosPriv);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useCancelBooking() {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ bookingId, reasonId, resonNote }) =>
            bookingsApi.cancelBooking(axiosPriv, bookingId, reasonId, resonNote),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            queryClient.invalidateQueries({ queryKey: ["customerBookingsDetails"] });
        },
    });
}

export function useCreateBookingMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();
    return useMutation({
        ...options,
        onSuccess: (data, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ["bookings"] });
            queryClient.invalidateQueries({ queryKey: ["booking-list"] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: (body) => bookingsApi.createBooking(axiosPriv, body),
        onError: onServerError,
    });
}

export function useUpdateBookingMutation(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();
    const { onServerError } = useFormServerSideErrorsHandler();

    return useMutation({
        ...options,
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["bookings"] });
            await queryClient.invalidateQueries({ queryKey: ["bookings", variables.bookingId] });
            options.onSuccess?.(data, variables, context);
        },
        mutationFn: ({ bookingId, data }) => bookingsApi.updateBooking(axiosPriv, bookingId, data),
        onError: onServerError,
    });
}

export function useCustomerBookings(customerId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const params = new URLSearchParams();
    if (customerId) {
        params.set("customer_id", customerId);
    }

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["customerBookings", customerId],
        queryFn: () => {
            return bookingsApi.getBookings(axiosPriv, params);
        },
        enabled: !!customerId,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useCustomerBookingsDetails(
    customerId,
    params = new URLSearchParams(),
    options = {},
) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["customerBookingsDetails", customerId, Object.fromEntries(params.entries())],
        queryFn: () => {
            return bookingsApi.getCustomerBookings(axiosPriv, customerId, params);
        },
        enabled: !!customerId,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useProductBookings(productId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    const params = new URLSearchParams();
    if (productId) {
        params.set("product_id", productId);
    }

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["customerBookings", productId],
        queryFn: () => {
            return bookingsApi.getBookings(axiosPriv, params);
        },
        enabled: !!productId,
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useLateBookings(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const { user } = useAuth();

    // Calculate dates for filtering
    const today = new Date();
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    // Format dates as ISO strings
    const todayStr = today.toISOString().split("T")[0];
    const twoDaysAgoStr = twoDaysAgo.toISOString().split("T")[0];

    // Set up search params
    const params = new URLSearchParams();
    params.set("creator", user.user_id);
    params.set("status", "Completed");
    params.set("dateTo_after", twoDaysAgoStr);
    params.set("dateTo_before", todayStr);

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["lateBookings", user?.id],
        queryFn: () => {
            return bookingsApi.getBookings(axiosPriv, params);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function usePostponedBookings(options = {}) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["postponedBookings"],
        queryFn: () => {
            return bookingsApi.getPostponedBookings(axiosPriv);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}
